import { useEffect, useRef, useState } from 'react';
import { useUserStats } from './useUserStats';

export interface XpGainEvent {
  token: number;          // increments per gain — consumers key off this
  amount: number;         // XP gained (Δ totalPoints)
  domainId: string | null;
  userFrom: number;       // user-level progress % before
  userTo: number;         // …after
  userLeveledUp: boolean;
  domainFrom: number;     // domain-level progress % before
  domainTo: number;       // …after
  domainLeveledUp: boolean;
}

// Don't react until totals are quiet for QUIET ms AND ≥ MIN_SETTLE ms since mount —
// covers the localStorage hydration burst and the one-time async cloud sync so
// neither reads as a "gain" on load.
const QUIET = 800;
const MIN_SETTLE = 2500;

interface CatSnap { points: number; progress: number; level: number }

/**
 * Detects real XP gains (a rise in stats.totalPoints) and returns the latest gain
 * as an event: how much, which domain, and the before/after progress for both the
 * user level and that domain. Decreases and load/sync settling are ignored.
 */
export function useXpGain(): XpGainEvent | null {
  const { stats } = useUserStats();
  const [event, setEvent] = useState<XpGainEvent | null>(null);

  const prevTotal = useRef<number | null>(null);
  const prevUserProg = useRef(stats.progress);
  const prevUserLevel = useRef(stats.level);
  const prevCats = useRef<Record<string, CatSnap>>({});
  const ready = useRef(false);
  const stabilize = useRef<number | null>(null);
  const mountTime = useRef(0);
  const tokenRef = useRef(0);

  const snapshot = () => {
    prevTotal.current = stats.totalPoints;
    prevUserProg.current = stats.progress;
    prevUserLevel.current = stats.level;
    const m: Record<string, CatSnap> = {};
    stats.categories.forEach((c) => { m[c.id] = { points: c.points, progress: c.progress, level: c.level }; });
    prevCats.current = m;
  };

  const armReady = () => {
    const elapsed = Date.now() - mountTime.current;
    if (elapsed >= MIN_SETTLE) ready.current = true;
    else stabilize.current = window.setTimeout(armReady, MIN_SETTLE - elapsed);
  };

  useEffect(() => {
    mountTime.current = Date.now();
    return () => { if (stabilize.current) clearTimeout(stabilize.current); };
  }, []);

  useEffect(() => {
    const total = stats.totalPoints;

    if (!ready.current) {
      snapshot();
      if (stabilize.current) clearTimeout(stabilize.current);
      stabilize.current = window.setTimeout(armReady, QUIET);
      return;
    }

    if (prevTotal.current !== null && total <= prevTotal.current) {
      snapshot();
      return;
    }

    const amount = prevTotal.current !== null ? total - prevTotal.current : 0;

    // Domain = the category whose points rose the most.
    let domainId: string | null = null;
    let best = 0;
    stats.categories.forEach((c) => {
      const prev = prevCats.current[c.id];
      const inc = prev ? c.points - prev.points : 0;
      if (inc > best) { best = inc; domainId = c.id; }
    });
    const domCat = domainId ? stats.categories.find((c) => c.id === domainId) : null;
    const domPrev = domainId ? prevCats.current[domainId] : undefined;

    const ev: XpGainEvent = {
      token: ++tokenRef.current,
      amount,
      domainId,
      userFrom: prevUserProg.current,
      userTo: stats.progress,
      userLeveledUp: stats.level !== prevUserLevel.current,
      domainFrom: domPrev ? domPrev.progress : (domCat ? domCat.progress : 0),
      domainTo: domCat ? domCat.progress : 0,
      domainLeveledUp: !!(domCat && domPrev && domCat.level !== domPrev.level),
    };

    snapshot();
    setEvent(ev);
  }, [stats.totalPoints, stats.progress, stats.level, stats.categories]);

  return event;
}
